import ApiService from '@/services/apiService';
import tenantPickerMixin from './pickerMixin';
import { mapState } from 'vuex';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import pullAt from 'lodash/pullAt';
import get from 'lodash/get';
import set from 'lodash/set';
export default {
  name: 'CoreTenantDetails',
  mixins: [tenantPickerMixin],
  model: {
    event: 'modified'
  },
  props: {
    // the tenant to be edited
    value: {
      type: Object,
      required: false,
      default: () => ({})
    },
    referenceTenant: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data: function () {
    return {
      valid: false,
      panel: null,
      isCreatingNewTenant: false,
      isLoading: false,
      defaultPasswordPolicy: {},
      mailConnectors: [],
      lclModel: {
        name: null,
        data: {
          email: {
            defaults: {},
            selectedMailConnector: null,
            useMailConnectorSetting: false
          },
          passwordPolicy: {},
          serviceBuilderUrl: null
        }
      },
      tenantDetails: {
        ancestors: []
      },
      tenantParentId: -1,
      tenantSwitchPrompt: false,
      tenantSwitchId: -1,
      passwordLengthRule: [v => !!v && !isNaN(parseInt(v)) && parseInt(v) >= this.defaultPasswordPolicy.length || this.$i18n_tc('accMgmt.user.passwordErrors.USER_PASSWORD_MINLENGTH', this.defaultPasswordPolicy.length, {
        n: this.defaultPasswordPolicy.length
      })],
      serviceBuilderParams: null,
      serviceBuilderPanel: null,
      currentUser2FAEnabled: undefined
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme', 'permissions']),
    ...mapState('authentication', ['user']),
    currentUserHas2FAEnabled() {
      return this.currentUser2FAEnabled;
    },
    isMandatory2FAToggleDisabled() {
      return !this.currentUserHas2FAEnabled;
    },
    requiredRule() {
      return [v => {
        return !!v || this.$i18n_t('Common.fieldRequired');
      }];
    },
    tenantDates() {
      moment.locale(this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language);
      // Use moment now that the locale has been properly set.
      let createdAt = moment(this.lclModel.createdAt).format('LLLL');
      let updatedAt = moment(this.lclModel.updatedAt).format('LLLL');
      return {
        createdAt,
        updatedAt
      };
    },
    tenantDomains() {
      return get(this.lclModel, 'data.theme.domains');
    },
    requiredEmailFieldsAreEmpty() {
      return !this.lclModel.data.email.emailAddress && !this.lclModel.data.email.serverAddress && !this.lclModel.data.email.serverPort && !this.lclModel.data.email.serverUser && !this.lclModel.data.email.serverPassword && this.lclModel.data.email.secure === undefined && this.lclModel.data.email.ignoreTLS === undefined && this.lclModel.data.email.rejectUnauthorized === undefined;
    },
    validateEmailSettings() {
      return value => {
        return this.requiredEmailFieldsAreEmpty ? !value : !!value;
      };
    },
    newTenantParentName() {
      return (this.tenants.find(t => t.id === this.tenantParentId) || {}).name;
    },
    hasMailConnectors() {
      return this.mailConnectors && this.mailConnectors.length;
    }
  },
  watch: {
    value() {
      this.fetchBridges();
      return this.fetchTenant().then(() => {
        this.fetchCurrentUser2FA();
        return this.createInitialData();
      });
    },
    tenantParentId() {
      this.lclModel.newParentId = this.tenantParentId;
    }
  },
  mounted() {
    return this.fetchTenant().then(() => {
      this.fetchBridges();
      this.fetchCurrentUser2FA();
      return this.createInitialData();
    });
  },
  methods: {
    cloneDeep,
    get,
    set,
    clearEmailSettings() {
      this.lclModel.data.email.emailAddress = undefined;
      this.lclModel.data.email.serverAddress = undefined;
      this.lclModel.data.email.serverPort = undefined;
      this.lclModel.data.email.serverUser = undefined;
      this.lclModel.data.email.serverPassword = undefined;
      this.lclModel.data.email.secure = undefined;
      this.lclModel.data.email.ignoreTLS = undefined;
      this.lclModel.data.email.rejectUnauthorized = undefined;
    },
    updateEmailSwitchToggle(e, item) {
      this.lclModel.data.email[item] = e;
      this.lclModel.data.email = cloneDeep(this.lclModel.data.email);
      this.revalidateForm();
    },
    revalidateForm() {
      this.$refs && this.$refs.form ? this.$refs.form.validate() : null;
    },
    updateDomains(domains) {
      set(this.lclModel, 'data.theme.domains', domains);
    },
    openLink() {
      window.open(this.lclModel.data.serviceBuilderUrl);
    },
    validate() {
      this.valid = this.$refs && this.$refs.form ? this.$refs.form.validate() : false;
      if (this.valid) {
        this.snackbar = true;
      }
      return this.valid;
    },
    createInitialData() {
      if (this.value && this.value.id) {
        this.lclModel = cloneDeep(this.value);
      }
      set(this.lclModel, 'data', get(this.lclModel, 'data', {}));
      set(this.lclModel, 'data.email', get(this.lclModel, 'data.email', {
        defaults: {}
      }));
      set(this.lclModel, 'data.twoFactorAuthenticationRequired', get(this.lclModel, 'data.twoFactorAuthenticationRequired', false));
      set(this.lclModel, 'data.passwordPolicy', {
        ...this.defaultPasswordPolicy,
        ...get(this.lclModel, 'data.passwordPolicy', this.defaultPasswordPolicy)
      });
      this.createInitialUserDefaultProperties();
    },
    createInitialUserDefaultProperties() {
      if (get(this.lclModel, 'data.userDefaultProperties', []).length === 0) {
        set(this.lclModel, 'data.userDefaultProperties', ['']);
      }
    },
    addUserProperty() {
      this.lclModel.data.userDefaultProperties = this.lclModel.data.userDefaultProperties.concat(['']);
      this.lclModel.data = cloneDeep(this.lclModel.data);
    },
    removeUserProperty(index) {
      pullAt(this.lclModel.data.userDefaultProperties, [index]);
      if (this.lclModel.data.userDefaultProperties.length === 0) {
        this.addUserProperty();
      }
      // trigger a UI refresh
      this.lclModel.data.userDefaultProperties = cloneDeep(this.lclModel.data.userDefaultProperties);
    },
    handleSubmit() {
      if (!this.validate()) {
        return;
      }
      this.lclModel.data.userDefaultProperties = this.lclModel.data.userDefaultProperties.filter(key => key.trim() !== '');
      if (this.lclModel.id) {
        this.updateTenant();
      } else if (this.referenceTenant) {
        this.createTenant();
      }
    },
    updateTenant() {
      // to be sure, also persist the bridges if there were any changes that the user did not save
      let bridgePromise = Promise.resolve();
      if (this.$refs['bridges']) {
        bridgePromise = this.$refs['bridges'].persistBridges();
      }
      return bridgePromise.then(() => {
        return ApiService.tenantUpdate(this, this.lclModel);
      }).then(() => {
        this.$snack(this.$i18n_t('accMgmt.tenant.tenantSuccessfulUpdate'), 'success');
        this.$emit('modified', cloneDeep(this.lclModel));
      }).catch(error => {
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(this.$i18n_t('accMgmt.tenant.tenantFailToUpdate'), 'error');
        }
      });
    },
    createTenant() {
      this.lclModel.parentId = this.referenceTenant.id;
      this.isCreatingNewTenant = true;
      return ApiService.tenantCreate(this, this.lclModel).then(res => {
        this.$store.state.app.dtwins = {};
        this.$snack(this.$i18n_t('accMgmt.tenant.tenantSuccessfulCreate'), 'success');
        this.$emit('created', res);
      }).catch(error => {
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(this.$i18n_t('accMgmt.tenant.tenantFailToCreate'), 'error');
        }
      }).finally(() => {
        this.isCreatingNewTenant = false;
      });
    },
    promptSelectedTenant(e) {
      this.tenantSwitchPrompt = true;
      this.tenantSwitchId = e;
    },
    fetchBridges() {
      this.mailConnectors = [];
      if (this.referenceTenant.id === null) {
        return;
      }
      ApiService.getBridgesForTenant(this.referenceTenant.id).then(result => {
        this.mailConnectors = result.filter(bridgeInstance => {
          return bridgeInstance.type === 'bridge-mail';
        }).map(bridge => {
          return bridge.state === 'active' ? bridge : {
            ...bridge,
            name: `${bridge.name} (inactive)`,
            disabled: true
          };
        });
        if (!this.lclModel.data.email.selectedMailConnector) {
          this.lclModel.data.email.selectedMailConnector = this.mailConnectors[0];
        }
      }).catch(error => {
        console.error('Error fetching bridges', error);
        this.$snack(this.$i18n_t('Connectors.Connectors.errors.connectionError'), 'error');
      });
    },
    fetchTenant() {
      let passwordPolicyPromise = ApiService.tenantGetPasswordPolicy().then(passwordPolicy => {
        this.defaultPasswordPolicy = passwordPolicy;
      });
      return passwordPolicyPromise.then(() => {
        if (this.referenceTenant) {
          return this.loadTenants(tenant => {
            return parseInt(tenant.id) !== parseInt(this.referenceTenant.id) && parseInt(tenant.parentId) !== parseInt(this.referenceTenant.id);
          });
        }
        return null;
      }).then(() => {
        if (this.referenceTenant) {
          this.tenantParentId = this.referenceTenant.parentId;
          return ApiService.tenantGetById(this.referenceTenant.id, ['no-scope-descendents']).then(tenant => {
            this.tenantDetails = tenant;
            this.revalidateForm();
            this.fetchServiceBuilderParams(this.referenceTenant.id);
          });
        }
        return null;
      }).catch(error => {
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack('Failed to fetch tenant', 'error');
        }
      });
    },
    fetchCurrentUser2FA() {
      var _this$user;
      if (!((_this$user = this.user) !== null && _this$user !== void 0 && _this$user.id)) {
        return;
      }
      ApiService.getUser(this).then(user => {
        const enabled = get(user, 'properties.2fa.enabled', false);
        this.currentUser2FAEnabled = enabled;
      }).catch(() => {
        this.currentUser2FAEnabled = false;
      });
    },
    fetchServiceBuilderParams(tenantId) {
      if (get(this.tenantDetails, 'data.serviceBuilderUrl') && this.permissions['super.system.manage']) {
        ApiService.tenantGetServiceBuilderParams(tenantId).then(params => {
          this.serviceBuilderParams = params;
        }).catch(error => {
          console.error(error);
        });
      } else {
        this.serviceBuilderParams = null;
      }
    }
  }
};